import type { IntegrationRequestDetail } from '../../../models/IntegrationRequest';
import { NotificationSeverity } from '@/components/notifications/Notification';
import type { StepResult } from '@/pages/TechChecks/models';
import { HAS_INTEGRATION_TEST_ENVIRONMENT } from '@/utils';
import { useNotification } from '@/hooks';
import { useState } from 'react';
import {
  IntegrationRequestStepId,
  TEST_ENVIRONMENT_STEPS,
  type IntegrationPrepareResult,
  type IntegrationRequestStep,
  type IntegrationStepRecord,
} from '../../../models/IntegrationRun';
import {
  useExportIntegrationPackageMutation,
  useImportIntegrationPackageInTestMutation,
  useIntegrateIntegrationRequestMutation,
  useRecordIntegrationRequestStepMutation,
} from '../../../services';
import { apiErrorMessage } from '../config';
import useSmokeTests from './useSmokeTests';

const SERVER_STEPS = [
  IntegrationRequestStepId.READINESS,
  IntegrationRequestStepId.BRANCH_CODES,
  IntegrationRequestStepId.PREPARE,
];

const summarizeSmokeResults = (results: StepResult[]) =>
  results.map(({ name, method, status, ms, state, error }) => ({
    name,
    method,
    status,
    ms,
    state,
    error,
  }));

const serverStepsOk = (steps: IntegrationRequestStep[]) =>
  SERVER_STEPS.every((id) => steps.some((step) => step.id === id && step.state === 'ok')) &&
  steps.some((step) => step.id === IntegrationRequestStepId.WEBHOOK_APPLY && step.state !== 'failed');

const useIntegrationRun = (request: IntegrationRequestDetail) => {
  const { showNotification } = useNotification();
  const [integrate] = useIntegrateIntegrationRequestMutation();
  const [recordStep] = useRecordIntegrationRequestStepMutation();
  const [exportPackage] = useExportIntegrationPackageMutation();
  const [importInTest] = useImportIntegrationPackageInTestMutation();
  const smokeTests = useSmokeTests();
  const [isRunning, setIsRunning] = useState(false);
  const [simulation, setSimulation] = useState<IntegrationRequestStep[] | null>(null);

  const record = (step: IntegrationStepRecord) =>
    recordStep({ requestId: request.id, step }).unwrap();

  const skipTestEnvironment = async () => {
    for (const id of TEST_ENVIRONMENT_STEPS) {
      await record({
        id,
        state: 'skipped',
        detail: { reason: 'Este país no tiene ambiente de test' },
      });
    }
  };

  const copyToTestAndVerify = async (prepare: IntegrationPrepareResult) => {
    if (!HAS_INTEGRATION_TEST_ENVIRONMENT) {
      await skipTestEnvironment();
      return;
    }

    let imported;
    try {
      const integrationPackage = await exportPackage(request.id).unwrap();
      imported = await importInTest({ integrationPackage, dryRun: false }).unwrap();
    } catch (error) {
      await record({
        id: IntegrationRequestStepId.SYNC_TEST,
        state: 'failed',
        error: apiErrorMessage(error, 'No se pudo copiar a test'),
      });
      return;
    }

    if (!imported.imported) {
      await record({
        id: IntegrationRequestStepId.SYNC_TEST,
        state: 'failed',
        detail: imported,
        error: [...imported.missingDependencies, ...imported.conflicts].join(' · '),
      });
      return;
    }
    await record({ id: IntegrationRequestStepId.SYNC_TEST, state: 'ok', detail: { plan: imported.plan } });

    if (imported.differences.length) {
      await record({
        id: IntegrationRequestStepId.VERIFY_TEST,
        state: 'failed',
        detail: { differences: imported.differences },
        error: 'Test quedó distinto a producción',
      });
      return;
    }
    await record({ id: IntegrationRequestStepId.VERIFY_TEST, state: 'ok', detail: { differences: [] } });

    const results = await smokeTests.run(prepare);
    const failed = results.find((result) => result.state !== 'ok');
    await record({
      id: IntegrationRequestStepId.SMOKE_TESTS,
      state: failed ? 'failed' : 'ok',
      detail: { results: summarizeSmokeResults(results) },
      error: failed ? `${failed.name}: ${failed.error}` : null,
    });
  };

  const withRunning = async (action: () => Promise<void>, errorMessage: string) => {
    setIsRunning(true);
    try {
      await action();
    } catch (error) {
      showNotification(apiErrorMessage(error, errorMessage), NotificationSeverity.error);
    } finally {
      setIsRunning(false);
    }
  };

  const runAll = () =>
    withRunning(async () => {
      setSimulation(null);
      const result = await integrate({ requestId: request.id, dryRun: false }).unwrap();
      const prepare = result.request?.result?.prepare;
      if (prepare && serverStepsOk(result.steps)) {
        await copyToTestAndVerify(prepare);
      }
    }, 'No se pudo integrar la solicitud');

  const retry = () => {
    const prepare = request.result?.prepare;
    if (prepare && serverStepsOk(request.steps)) {
      return withRunning(() => copyToTestAndVerify(prepare), 'No se pudo copiar a test');
    }
    return runAll();
  };

  const simulate = () =>
    withRunning(async () => {
      const result = await integrate({ requestId: request.id, dryRun: true }).unwrap();
      setSimulation(result.steps);
    }, 'No se pudo simular la integración');

  return {
    runAll,
    retry,
    simulate,
    simulation,
    isRunning,
    smokeResults: smokeTests.results,
  };
};

export type IntegrationRunState = ReturnType<typeof useIntegrationRun>;

export default useIntegrationRun;
